/**
 * PRP-MCP-12 web client for the Macros page: the signed-in user's own macros,
 * their lines, and macro file upload (Xactimate .MCX export).
 *
 * Contract: app/api/routes/mcp_macros.py (mounted under /api/v1/mcp). A macro
 * carries no price; nothing here reads or sends one. Failures arrive as
 * `ApiError` (message = the backend `detail`).
 */
import { api } from './api';

/** Policy action names (app/mcp/data/policy.json "actions"). */
export const MACRO_ACTIONS = [
  'remove',
  'replace',
  'remove_and_replace',
  'detach_and_reset',
  'material_only',
  'install_only',
] as const;
export type MacroAction = (typeof MACRO_ACTIONS)[number];

/** Plain labels for the actions. */
export const MACRO_ACTION_LABELS: Record<MacroAction, string> = {
  remove: 'Remove',
  replace: 'Replace',
  remove_and_replace: 'Remove and replace',
  detach_and_reset: 'Detach and reset',
  material_only: 'Material only',
  install_only: 'Install only',
};

/** One macro in the list. */
export interface MacroSummary {
  macro_id: string;
  name: string;
  description: string | null;
  /** Line count. */
  lines: number;
  source: 'room' | 'chat' | 'file';
  source_ref: string | null;
  created_at: string;
  updated_at: string;
  last_used_at: string | null;
}

/** One line of a macro. Quantity or calc (or neither: asked when applied). */
export interface MacroLine {
  line_id: string;
  position: number;
  code: string;
  description: string | null;
  action: MacroAction | null;
  quantity: number | null;
  calc: string | null;
  unit: string | null;
  heading: string | null;
  note: string | null;
  /** Plain-language warnings: unknown code, no action, a calc AIEA cannot read. */
  flags?: string[];
}

/** A macro with its lines. */
export interface MacroDetail extends Omit<MacroSummary, 'lines'> {
  lines: MacroLine[];
  line_count: number;
}

/** What a line edit sends. */
export interface MacroLineInput {
  code: string;
  action: MacroAction;
  quantity?: number | string | null;
  calc?: string | null;
  heading?: string | null;
  note?: string | null;
}

/** One imported macro in an upload result. */
export interface MacroImported {
  name: string;
  macro_id: string;
  lines: number;
  unknown_codes: string[];
  replaced: boolean;
  lines_without_action: number;
  lines_without_amount: number;
}

/** POST /mcp/macros/upload. */
export interface MacroUploadResult {
  upload_id: string;
  file: string | null;
  imported: MacroImported[];
  skipped: { name: string; reason: string }[];
  prices_dropped: number;
  unknown_codes: string[];
  errors: string[];
}

export const MacrosAPI = {
  list: (): Promise<{ macros: MacroSummary[] }> => api.get('/mcp/macros'),

  get: (id: string): Promise<MacroDetail> => api.get(`/mcp/macros/${encodeURIComponent(id)}`),

  /** Rename and/or change the description. */
  update: (id: string, patch: { name?: string; description?: string }): Promise<MacroDetail> =>
    api.patch(`/mcp/macros/${encodeURIComponent(id)}`, patch),

  remove: (id: string): Promise<{ deleted: string }> =>
    api.delete(`/mcp/macros/${encodeURIComponent(id)}`),

  addLine: (id: string, line: MacroLineInput): Promise<MacroLine> =>
    api.post(`/mcp/macros/${encodeURIComponent(id)}/lines`, line),

  updateLine: (id: string, lineId: string, line: MacroLineInput): Promise<MacroLine> =>
    api.patch(`/mcp/macros/${encodeURIComponent(id)}/lines/${encodeURIComponent(lineId)}`, line),

  removeLine: (id: string, lineId: string): Promise<MacroDetail> =>
    api.delete(`/mcp/macros/${encodeURIComponent(id)}/lines/${encodeURIComponent(lineId)}`),

  /** Uploads one Xactimate macro export; every macro in it is imported at once. */
  upload: (file: File): Promise<MacroUploadResult> => {
    const form = new FormData();
    form.append('file', file);
    return api.post('/mcp/macros/upload', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};
